import { ClassItem, GenreType, VisibilityType } from '../types';
import { api } from '../services/api';
import { captureThumbnail, generateGenreFallback } from '../utils/thumbnail';
import { showToast } from '../utils/toast';
import { promptConflict } from '../components/ConflictModal';
import { promptConsent } from '../components/ConsentModal';

export function renderSubmitView(
  container: HTMLElement,
  currentClass: ClassItem,
  onNavigate: (path: string) => void
): void {
  let selectedFile: File | null = null;
  let fileContent: string = '';
  let generatedThumbnailUrl: string = '';
  let selectedVisibility: VisibilityType = 'private';
  let isGeneratingThumb: boolean = false;

  container.innerHTML = `
    <!-- Google Classroom ヒーローバナー -->
    <section class="hero-banner theme-${currentClass.themeColor}" aria-label="授業情報">
      <h1 class="banner-title">${currentClass.name}</h1>
      <p class="banner-desc">${currentClass.description || 'HTML作品の提出・リアルタイム鑑賞スペース'}</p>
      <div class="banner-meta">
        <div class="banner-pill" id="btn-copy-url" title="提出箱URLをコピー">
          <span>📋 提出箱URLをコピー</span>
        </div>
        <div class="banner-pill">
          <span>🔑 パスフレーズ認証済み</span>
        </div>
      </div>
    </section>

    <!-- Classroom風 タブバー -->
    <nav class="tab-bar" aria-label="ナビゲーション">
      <button class="tab-btn active" id="tab-submit" type="button">
        <span>📤 提出箱</span>
      </button>
      <button class="tab-btn" id="tab-library" type="button">
        <span>📚 授業用ライブラリ</span>
      </button>
    </nav>

    <!-- 提出フォーム -->
    <div class="submit-panel">
      <h2 style="font-size: 18px; font-weight: 500; margin-bottom: 8px;">作品の提出（ドラッグ＆ドロップ）</h2>
      <p style="color: var(--color-text-secondary); margin-bottom: 24px; font-size: 13px;">
        作成した単独のHTMLファイル（.html）をドロップしてください。元ファイル名を保持してWeb公開されます。
      </p>

      <!-- ドロップゾーン -->
      <div class="dropzone" id="dropzone" tabindex="0" role="button" aria-label="HTMLファイルのドラッグ＆ドロップエリア">
        <input type="file" id="file-input" accept=".html,.htm" style="display: none;" />
        <div class="dropzone-icon" id="dropzone-icon">📁</div>
        <div class="dropzone-title" id="dropzone-title">HTMLファイルをここにドラッグ＆ドロップ</div>
        <div class="dropzone-subtitle" id="dropzone-subtitle">またはクリックしてファイルを選択（最大5MB / .html, .htm）</div>
      </div>

      <!-- サムネイル生成状況 / プレビュー -->
      <div id="thumb-preview-area" style="display: none;" class="thumbnail-preview-box">
        <img id="thumb-img" class="thumbnail-preview-img" alt="サムネイルプレビュー" />
        <div class="thumbnail-preview-meta">
          <p style="font-weight: 600; font-size: 13px; margin-bottom: 4px;" id="thumb-status-text">
            ✨ サムネイル自動生成完了
          </p>
          <p style="font-size: 12px; color: var(--color-text-secondary);" id="thumb-note-text">
            （16:9 サムネイル / 外部通信なしで安全に描画）
          </p>
        </div>
      </div>

      <!-- メタデータ入力フォーム -->
      <form id="submission-form">
        <div class="form-grid">
          <div class="form-group full-width">
            <label class="form-label" for="input-title">
              アプリ名 / 作品名
              <span class="badge-req">必須 (最大40文字)</span>
            </label>
            <input
              type="text"
              id="input-title"
              class="form-input"
              maxlength="40"
              placeholder="例: ローマ字タイピングゲーム"
              required
            />
          </div>

          <div class="form-group">
            <label class="form-label" for="select-genre">
              ジャンル
              <span class="badge-req">必須</span>
            </label>
            <select id="select-genre" class="form-select" required>
              <option value="ゲーム">ゲーム</option>
              <option value="ツール">ツール</option>
              <option value="アニメーション">アニメーション</option>
              <option value="ポートフォリオ">ポートフォリオ</option>
              <option value="アート">アート</option>
              <option value="その他">その他</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label" for="input-author">
              制作者名 / ニックネーム
              <span class="badge-opt">任意 (未入力時は「匿名」)</span>
            </label>
            <input
              type="text"
              id="input-author"
              class="form-input"
              maxlength="20"
              placeholder="例: 向井"
            />
          </div>

          <div class="form-group full-width">
            <label class="form-label" for="input-description">
              作品説明・見どころ
              <span class="badge-opt">任意 (最大200文字)</span>
            </label>
            <textarea
              id="input-description"
              class="form-textarea"
              maxlength="200"
              placeholder="工夫した点や操作方法などを入力してください"
            ></textarea>
          </div>
        </div>

        <!-- 公開範囲選択（限定公開 vs 完全公開） -->
        <div style="margin-bottom: 12px;">
          <label class="form-label" style="margin-bottom: 8px;">
            公開範囲の選択
            <span class="badge-req">必須</span>
          </label>
          <div class="visibility-selector">
            <div class="visibility-card selected" id="vis-card-private">
              <input type="radio" name="visibility" value="private" id="vis-private" class="visibility-radio" checked />
              <div class="visibility-info">
                <h4>🔒 限定公開（推奨）</h4>
                <p>この授業の「授業用ライブラリ」でのみ公開されます。授業内の受講生同士での相互鑑賞に適しています。</p>
              </div>
            </div>

            <div class="visibility-card" id="vis-card-public">
              <input type="radio" name="visibility" value="public" id="vis-public" class="visibility-radio" />
              <div class="visibility-info">
                <h4>🌐 完全公開</h4>
                <p>「授業用ライブラリ」に加え、誰でも見られる「公開ライブラリ」にも掲載されます。（著作権同意が必要です）</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 提出アクション -->
        <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: 32px;">
          <button type="submit" id="btn-submit" class="btn btn-primary" style="height: 44px; padding: 0 32px; font-size: 15px;" disabled>
            🚀 作品を提出してWeb公開する
          </button>
        </div>
      </form>
    </div>
  `;

  // イベントハンドラ登録
  const copyBtn = container.querySelector('#btn-copy-url')!;
  copyBtn.addEventListener('click', () => {
    navigator.clipboard.writeText(window.location.href);
    showToast('提出箱のURLをクリップボードにコピーしました！', 'success');
  });

  const tabLibrary = container.querySelector('#tab-library')!;
  tabLibrary.addEventListener('click', () => {
    onNavigate(`/c/${currentClass.id}/library`);
  });

  const dropzone = container.querySelector('#dropzone') as HTMLElement;
  const fileInput = container.querySelector('#file-input') as HTMLInputElement;
  const submitBtn = container.querySelector('#btn-submit') as HTMLButtonElement;
  const thumbArea = container.querySelector('#thumb-preview-area') as HTMLElement;
  const thumbImg = container.querySelector('#thumb-img') as HTMLImageElement;
  const thumbStatus = container.querySelector('#thumb-status-text') as HTMLElement;

  const titleInput = container.querySelector('#input-title') as HTMLInputElement;
  const genreSelect = container.querySelector('#select-genre') as HTMLSelectElement;
  const authorInput = container.querySelector('#input-author') as HTMLInputElement;
  const descInput = container.querySelector('#input-description') as HTMLTextAreaElement;

  // 公開範囲カードの切り替え
  const cardPrivate = container.querySelector('#vis-card-private')!;
  const cardPublic = container.querySelector('#vis-card-public')!;
  const radioPrivate = container.querySelector('#vis-private') as HTMLInputElement;
  const radioPublic = container.querySelector('#vis-public') as HTMLInputElement;

  const setVisibility = async (vis: VisibilityType) => {
    if (vis === 'public') {
      const agreed = await promptConsent();
      if (!agreed) {
        // キャンセルされたら限定公開を維持
        radioPrivate.checked = true;
        cardPrivate.classList.add('selected');
        cardPublic.classList.remove('selected');
        selectedVisibility = 'private';
        return;
      }
    }

    selectedVisibility = vis;
    if (vis === 'private') {
      radioPrivate.checked = true;
      cardPrivate.classList.add('selected');
      cardPublic.classList.remove('selected');
    } else {
      radioPublic.checked = true;
      cardPublic.classList.add('selected');
      cardPrivate.classList.remove('selected');
    }
  };

  cardPrivate.addEventListener('click', () => setVisibility('private'));
  cardPublic.addEventListener('click', () => setVisibility('public'));

  // ファイルハンドリング
  const handleFile = async (file: File) => {
    if (!file.name.match(/\.(html|htm)$/i)) {
      showToast('HTMLファイル（.html または .htm）を選択してください', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('ファイルサイズは5MB以内にしてください', 'error');
      return;
    }

    selectedFile = file;

    // ファイル名サニタイズチェック
    let safeName = file.name.replace(/\s+/g, '_');
    if (!/^[a-zA-Z0-9_.-]+$/.test(safeName)) {
      showToast('ファイル名には半角英数字、ハイフン、アンダースコアを使用してください', 'error');
    }

    container.querySelector('#dropzone-icon')!.textContent = '📄';
    container.querySelector('#dropzone-title')!.textContent = file.name;
    container.querySelector('#dropzone-subtitle')!.textContent = `${(file.size / 1024).toFixed(1)} KB - 準備完了`;

    // アプリ名の自動推定（空の場合）
    if (!titleInput.value) {
      titleInput.value = file.name.replace(/\.(html|htm)$/i, '').replace(/[-_]/g, ' ');
    }

    // ファイル読み込み ＆ サムネイル生成
    fileContent = await file.text();
    thumbArea.style.display = 'flex';
    thumbStatus.textContent = '⏳ サムネイルを生成中... (最大10秒)';
    thumbImg.src = generateGenreFallback(genreSelect.value as GenreType, titleInput.value);

    isGeneratingThumb = true;
    try {
      const res = await captureThumbnail(
        fileContent,
        genreSelect.value as GenreType,
        titleInput.value || 'アプリ',
        10000 // 10秒タイムアウト
      );
      generatedThumbnailUrl = res.dataUrl;
      thumbImg.src = res.dataUrl;
      thumbStatus.textContent = res.isFallback
        ? '🎨 ジャンル別フォールバックサムネイルを適用'
        : '✨ サムネイル自動キャプチャ完了';
    } catch {
      generatedThumbnailUrl = generateGenreFallback(genreSelect.value as GenreType, titleInput.value);
      thumbImg.src = generatedThumbnailUrl;
      thumbStatus.textContent = '🎨 ジャンル別サムネイルを適用';
    } finally {
      isGeneratingThumb = false;
      submitBtn.disabled = false;
    }
  };

  dropzone.addEventListener('click', () => fileInput.click());
  dropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropzone.classList.add('dragover');
  });
  dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'));
  dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropzone.classList.remove('dragover');
    if (e.dataTransfer && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  });

  fileInput.addEventListener('change', () => {
    if (fileInput.files && fileInput.files.length > 0) {
      handleFile(fileInput.files[0]);
    }
  });

  // ジャンル変更時にサムネイルプレビューを再調整（フォールバック時）
  genreSelect.addEventListener('change', () => {
    if (!generatedThumbnailUrl || generatedThumbnailUrl.startsWith('data:image/svg')) {
      generatedThumbnailUrl = generateGenreFallback(genreSelect.value as GenreType, titleInput.value);
      thumbImg.src = generatedThumbnailUrl;
    }
  });

  // フォーム送信
  const form = container.querySelector('#submission-form') as HTMLFormElement;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (isGeneratingThumb) {
      showToast('サムネイル生成中です。少々お待ちください...', 'info');
      return;
    }
    if (!selectedFile) {
      showToast('HTMLファイルを選択してください', 'error');
      return;
    }

    const title = titleInput.value.trim();
    if (!title) {
      showToast('アプリ名を入力してください', 'error');
      return;
    }

    const fileName = selectedFile.name.replace(/\s+/g, '_');

    // 同名ファイルの競合チェック
    const isConflict = await api.checkConflict(currentClass.id, fileName);
    if (isConflict) {
      const action = await promptConflict(fileName);
      if (action === 'cancel') {
        showToast('提出をキャンセルしました。ファイル名を変更して再提出してください。', 'info');
        return;
      }
    }

    submitBtn.disabled = true;
    submitBtn.textContent = '📤 アップロード中...';

    try {
      const res = await api.uploadSubmission({
        classId: currentClass.id,
        fileName,
        fileContent,
        title,
        genre: genreSelect.value as GenreType,
        author: authorInput.value.trim() || '匿名',
        description: descInput.value.trim(),
        visibility: selectedVisibility,
        thumbnailUrl: generatedThumbnailUrl || generateGenreFallback(genreSelect.value as GenreType, title),
        fileSizeBytes: selectedFile.size
      });

      if (res.isOverwritten) {
        showToast(`「${fileName}」を上書き提出しました（旧ファイルはバックアップ退避）`, 'success');
      } else {
        showToast(`「${title}」の提出が完了しました！`, 'success');
      }

      // ライブラリへ自動遷移
      setTimeout(() => {
        onNavigate(`/c/${currentClass.id}/library`);
      }, 800);
    } catch (err) {
      console.error(err);
      showToast('アップロードに失敗しました', 'error');
      submitBtn.disabled = false;
      submitBtn.textContent = '🚀 作品を提出してWeb公開する';
    }
  });
}
